// Vanilla CSS for InputGroup (text/textarea/date/file/select/checkbox/switch +
// math-editor modal). Prefix: ig-. Aggregated into libraryStyles. The react-select
// `styles` prop is already plain JS objects and is left as-is.
export const inputGroupStyles = `
  .ig-field { margin-top: 0.25rem; margin-bottom: 0.25rem; }
  .ig-label {
    font-size: var(--fs-md);
    font-weight: 600;
    padding-left: 0.5rem;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    color: var(--catchup-gray-400);
  }
  .ig-count {
    font-size: var(--fs-md);
    font-weight: 600;
    padding-right: 0.5rem;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    color: var(--catchup-gray-400);
  }
  /* Over the cap the count is not trivia — it is why the save would be refused. */
  .ig-count.is-over {
    color: var(--color-error);
    font-weight: 800;
  }
  .ig-row-between {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  .ig-wfull { width: 100%; }
  .ig-pr20 { padding-right: 5rem; }

  /* checkbox */
  .ig-checkbox {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
  }
  /* designed glass checkbox glyph (reused in the activity solver) */
  .ig-cbx-box {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--rad-catchup-small);
    border: 1.5px solid var(--catchup-blue-300);
    background: linear-gradient(180deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.25) 100%);
    box-shadow: inset 0 1px 1px rgba(255,255,255,0.9), 0 1px 2px rgba(15,72,89,0.06);
    transition: all var(--duration-base) var(--ease-standard);
    cursor: pointer;
  }
  .ig-cbx-box:hover { border-color: var(--catchup-blue); }
  .ig-cbx-box.ig-cbx-on {
    border-color: var(--catchup-blue);
    background: linear-gradient(135deg, #73D7E6 0%, var(--catchup-blue) 55%, var(--brand-dark) 100%);
    box-shadow: inset 0 1px 1px rgba(255,255,255,0.55), 0 2px 5px -1px rgba(15,136,156,0.35);
  }
  .ig-cbx-box.ig-cbx-correct { border-color: var(--color-success); background: var(--color-success); }
  .ig-cbx-box.ig-cbx-wrong { border-color: var(--color-error); background: var(--color-error); }
  /* Graded multi-answer: ig-cbx-correct fills EVERY correct option's box, including
     ones the learner never ticked, so their own ticks carry a ring on top — a
     partly-right answer must not read as fully right. Same device as
     .mc-tf2-box.is-picked. */
  .ig-cbx-box.ig-cbx-picked { box-shadow: 0 0 0 2px var(--catchup-white), 0 0 0 4px var(--catchup-blue); }
  /* A correct option the learner did NOT tick: green OUTLINE, no fill and no tick,
     so a solid ticked box always means "I marked this one". */
  .ig-cbx-box.ig-cbx-key { border-color: var(--color-success); background: color-mix(in srgb, var(--color-success) 14%, transparent); }
  .ig-cbx-check { color: var(--catchup-white); display: flex; }

  /* file input */
  .ig-input {
    width: 100%;
    padding: 0.5rem 1rem;
    border: 1px solid var(--glass-flat-border);
    border-radius: var(--rad-catchup-large);
    color: var(--catchup-gray-600);
    /* Solid (no blur) — form fields repeat per form on scrolling screens;
       blur only frosts the flat page bg (pure raster cost). */
    background-color: var(--glass-flat-solid);
    box-shadow: var(--glass-flat-edge);
    transition: border-color var(--duration-base) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard);
  }
  .ig-input::placeholder { color: var(--catchup-gray-200); }
  .ig-input:focus {
    outline: none;
    border-color: var(--catchup-blue-400);
    box-shadow: var(--shadow-input);
  }

  /* date input */
  .ig-date {
    width: 100%;
    padding: 0.5rem 1rem;
    border-width: 1px;
    border-style: solid;
    border-radius: var(--rad-catchup-large);
    color: var(--catchup-gray-600);
    /* Solid (no blur) — form fields repeat per form on scrolling screens;
       blur only frosts the flat page bg (pure raster cost). */
    background-color: var(--glass-flat-solid);
    box-shadow: var(--glass-flat-edge);
  }
  .ig-date:focus { outline: none; box-shadow: var(--shadow-input); }
  .ig-date-error { border-color: var(--catchup-red); box-shadow: var(--shadow-error); }
  .ig-date-red {
    border-color: var(--catchup-red);
    background-color: var(--catchup-red);
    color: var(--catchup-white);
  }
  .ig-date-red:focus { border-color: var(--catchup-red); }
  .ig-date-default { border-color: var(--glass-flat-border); }
  .ig-date-default::placeholder { color: var(--catchup-gray-200); }
  .ig-date-default:focus { border-color: var(--catchup-blue-400); }

  /* textarea */
  .ig-ta-wrap {
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
    flex: 1 1 0%;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  .ig-ta {
    width: 100%;
    height: 44px;
    resize: none;
    overflow: hidden;
    padding: 0.5rem 1rem;
    border-width: 1px;
    border-style: solid;
    border-radius: var(--rad-catchup-large);
    /* Solid (no blur) — form fields repeat per form on scrolling screens;
       blur only frosts the flat page bg (pure raster cost). */
    background-color: var(--glass-flat-solid);
    box-shadow: var(--glass-flat-edge);
  }
  .ig-ta::placeholder { color: var(--catchup-gray-200); }
  .ig-ta:focus { outline: none; border-color: var(--catchup-blue-400); box-shadow: var(--shadow-input); }
  .ig-ta-minh { min-height: 250px; }
  .ig-ta-error { border-color: var(--catchup-red); box-shadow: var(--shadow-error); }
  .ig-ta-error::placeholder { color: var(--catchup-red); opacity: 0.8; }
  .ig-ta-default { border-color: var(--glass-flat-border); }
  .ig-ta-disabled { background-color: var(--catchup-lighter-gray); }

  /* text input */
  .ig-text-wrap { margin-top: 0.25rem; margin-bottom: 0.25rem; position: relative; }
  .ig-text-box {
    width: 100%;
    border-width: 1px;
    border-style: solid;
    border-radius: var(--rad-catchup-large);
    box-shadow: var(--glass-flat-edge);
  }
  .ig-text-box:focus-within { border-color: var(--catchup-blue-400); box-shadow: var(--shadow-input); }
  .ig-text-box-error { border-color: var(--catchup-red); box-shadow: var(--shadow-error); }
  .ig-text-box-default { border-color: var(--glass-flat-border); }
  .ig-text-box-disabled { background-color: var(--catchup-lighter-gray); }
  .ig-text-box-enabled {
    /* Solid (no blur) — form fields repeat per form on scrolling screens;
       blur only frosts the flat page bg (pure raster cost). */
    background-color: var(--glass-flat-solid);
  }
  .ig-text-input {
    width: 100%;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: var(--rad-catchup-large);
  }
  .ig-text-input:focus { outline: none; }
  .ig-text-input::placeholder { color: var(--catchup-gray-200); }
  .ig-text-input-disabled { background-color: var(--catchup-lighter-gray); }
  .ig-text-input-error::placeholder { color: var(--catchup-red); opacity: 0.8; }

  /* math-editor button */
  .ig-math-btn {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    background-color: var(--catchup-blue-400);
    color: #ffffff;
    border-radius: 0.375rem;
    padding: 0.25rem 0.75rem;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    transition: background-color 200ms, color 200ms;
  }
  .ig-math-btn:hover { background-color: var(--catchup-blue-500); }
  .ig-z10 { z-index: 10; }
  .ig-math-btn-inner { display: flex; align-items: center; gap: 0.25rem; }
  .ig-math-btn-label { font-size: var(--fs-sm); font-weight: 500; }
  .ig-icon3 { width: 0.75rem; height: 0.75rem; }

  /* math-editor modal */
  .ig-math-wrap { background-color: var(--catchup-white); border-radius: 0.5rem; overflow: hidden; }
  .ig-math-stack > * + * { margin-top: 1.5rem; }
  .ig-math-hint { font-size: var(--fs-md); color: var(--catchup-gray-400); padding-left: 0.5rem; }
  .ig-math-table-wrap {
    background-color: var(--catchup-gray-50);
    border-width: 1px;
    border-style: solid;
    border-color: var(--catchup-gray-100);
    border-radius: var(--rad-catchup-large);
    padding: 1rem;
  }
  .ig-math-table { width: 100%; font-size: var(--fs-md); }
  .ig-math-thead { text-align: left; color: var(--catchup-gray-400); }
  .ig-math-th { padding-bottom: 0.5rem; font-weight: 600; }
  .ig-math-tbody { font-family: monospace; color: var(--catchup-gray-400); }
  .ig-math-td { padding-top: 0.25rem; padding-bottom: 0.25rem; }

  /* switch */
  .ig-switch-wrap { display: flex; flex-direction: column; margin-top: 0.25rem; margin-bottom: 0.25rem; }
  .ig-switch-row { display: flex; flex-direction: row; align-items: center; }
  .ig-switch-label { padding-left: 0.5rem; padding-right: 0.5rem; transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1); }
  .ig-switch-on { font-weight: 700; }
  .ig-switch-off { color: var(--catchup-gray); }
  .ig-switch-desc {
    font-size: var(--fs-md);
    color: var(--catchup-gray-300);
    margin-top: 0.25rem;
    margin-left: 3rem;
  }
`;
